/**
 * Mise en contexte des mesures du tableau de bord.
 *
 * Une mesure isolée ne veut rien dire : « a dashboard must put measures into
 * context by including meaningful comparisons » (Stephen Few, *Information
 * Dashboard Design*). Chaque indicateur sort donc d'ici avec sa fenêtre
 * courante, la fenêtre précédente pour l'écart, et douze compartiments
 * hebdomadaires pour la sparkline (Tufte : un graphique de la taille d'un mot,
 * sans axe ni étiquette, qui ne sert qu'à donner la direction).
 *
 * Module volontairement pur (aucune dépendance serveur) : la page décide où
 * les dates sont lues, le calcul est le même pour un espace personnel et pour
 * un espace client consulté par l'équipe.
 */

export const TREND_WEEKS = 12;
export const WINDOW_DAYS = 30;

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;

export interface Trend {
  /** Occurrences sur les WINDOW_DAYS derniers jours. */
  value: number;
  /** Occurrences sur les WINDOW_DAYS jours précédents, pour l'écart. */
  previous: number;
  /** TREND_WEEKS compartiments hebdomadaires, du plus ancien à la semaine en cours. */
  series: number[];
}

export const emptyTrend = (): Trend => ({
  value: 0,
  previous: 0,
  series: new Array(TREND_WEEKS).fill(0),
});

/** Début de la période à charger : douze semaines couvrent les deux fenêtres. */
export function trendSince(now: Date) {
  return new Date(now.getTime() - TREND_WEEKS * WEEK_MS);
}

/** Répartit des horodatages en fenêtres de comparaison et en semaines. */
export function buildTrend(dates: Array<string | null | undefined>, now: Date): Trend {
  const trend = emptyTrend();
  const nowMs = now.getTime();

  for (const raw of dates) {
    if (!raw) continue;
    const time = Date.parse(raw);
    if (Number.isNaN(time)) continue;

    const age = nowMs - time;
    if (age < 0) continue; // horodatage futur : on ne l'invente pas

    const days = age / DAY_MS;
    if (days < WINDOW_DAYS) trend.value += 1;
    else if (days < WINDOW_DAYS * 2) trend.previous += 1;

    const week = Math.floor(age / WEEK_MS);
    if (week < TREND_WEEKS) trend.series[TREND_WEEKS - 1 - week] += 1;
  }

  return trend;
}

export type DeltaDirection = "up" | "down" | "flat";

export interface Delta {
  direction: DeltaDirection;
  /** Signé, avec un vrai signe moins typographique. */
  label: string;
}

export function deltaOf(trend: Trend): Delta {
  const difference = trend.value - trend.previous;
  if (difference === 0) return { direction: "flat", label: "0" };
  return {
    direction: difference > 0 ? "up" : "down",
    label: `${difference > 0 ? "+" : "−"}${Math.abs(difference)}`,
  };
}

/**
 * Étapes du pipeline, dans l'ordre de progression : l'échelle est ordinale,
 * donc une seule teinte du clair au foncé (jamais quatre couleurs d'identité).
 * Rampe validée par `scripts/validate_palette.js --ordinal` : monotone en
 * clarté, écarts visibles, extrémité claire à 2,25:1 sur fond blanc.
 */
// Labels: `dashboard.pipeline.stages`, keyed by `key`.
export const PIPELINE_STAGES = [
  { key: "fresh" }, // jamais consultés
  { key: "toContact" }, // prêts à l’envoi
  { key: "engaged" }, // contactés ou en réponse
  { key: "won" }, // affaires signées
] as const;

export type PipelineStageKey = (typeof PIPELINE_STAGES)[number]["key"];
export type PipelineCounts = Record<PipelineStageKey, number>;
